/* src/serviceWorkerRegistration.js */

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/.test(window.location.hostname)
);

const isProd = process.env.NODE_ENV === "production";

// Guard agar tidak reload loop
const SW_PENDING_RELOAD_KEY = "sw:pending_reload";
const SW_RELOADED_ONCE_KEY = "sw:reloaded_once";

function safeCall(fn, ...args) {
  try {
    if (typeof fn === "function") return fn(...args);
  } catch {}
}

function isSecureContextForSW() {
  // SW butuh HTTPS atau localhost
  return window.isSecureContext || isLocalhost || window.location.protocol === "https:";
}

function isJsMime(ct = "") {
  const t = String(ct || "").toLowerCase();
  return (
    t.includes("javascript") ||
    t.includes("ecmascript") ||
    t === "application/x-javascript"
  );
}

function looksLikeHtml(text = "") {
  const s = String(text || "").slice(0, 400).toLowerCase();
  return s.includes("<!doctype html") || s.includes("<html");
}

function getPublicBasePath() {
  try {
    const pub = process.env.PUBLIC_URL || "/";
    const u = new URL(pub, window.location.origin);
    let p = u.pathname || "/";
    if (!p.startsWith("/")) p = "/" + p;
    if (!p.endsWith("/")) p += "/";
    return p;
  } catch {
    return "/";
  }
}

function buildSwUrl(basePath) {
  const u = new URL(`${basePath}service-worker.js`, window.location.origin);
  return u.toString();
}

function withTimeout(ms = 8000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), ms);
  return { controller, done: () => clearTimeout(t) };
}

function isSameOrigin(url) {
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

function markPendingReload() {
  try {
    sessionStorage.setItem(SW_PENDING_RELOAD_KEY, String(Date.now()));
  } catch {}
}

function clearReloadFlags() {
  try {
    sessionStorage.removeItem(SW_PENDING_RELOAD_KEY);
    sessionStorage.removeItem(SW_RELOADED_ONCE_KEY);
  } catch {}
}

function reloadOnce(delayMs = 150) {
  try {
    const pending = sessionStorage.getItem(SW_PENDING_RELOAD_KEY);
    const already = sessionStorage.getItem(SW_RELOADED_ONCE_KEY);

    // reload hanya jika memang update & belum reload
    if (!pending || already) return;

    sessionStorage.setItem(SW_RELOADED_ONCE_KEY, "1");

    // delay kecil biar controller benar2 stabil
    setTimeout(() => {
      // kalau masih stuck, hard reload (lebih aman untuk SW update)
      window.location.reload();
    }, Math.max(0, Number(delayMs) || 0));
  } catch {}
}

// Validasi SW: pastikan bukan HTML (dan same-origin)
async function validateServiceWorkerUrl(swUrl) {
  if (!isSameOrigin(swUrl)) {
    return { ok: false, reason: "SW URL harus same-origin" };
  }

  const probe = new URL(swUrl, window.location.origin);
  probe.searchParams.set("probe", "1");
  const probeUrl = probe.toString();

  // 1) HEAD (opsional)
  try {
    const { controller, done } = withTimeout(5000);
    const head = await fetch(probeUrl, {
      method: "HEAD",
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    }).catch(() => null);
    done();

    if (head && head.ok) {
      const ct = head.headers.get("content-type") || "";
      if (isJsMime(ct)) return { ok: true };
    }
  } catch {}

  // 2) GET (pasti)
  try {
    const { controller, done } = withTimeout(8000);
    const res = await fetch(probeUrl, {
      method: "GET",
      headers: { Accept: "text/javascript, application/javascript, */*" },
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    });
    done();

    if (res.type === "opaque") return { ok: false, reason: "Response opaque (CORS)" };
    if (!res.ok) return { ok: false, reason: `HTTP ${res.status}` };

    const ct = res.headers.get("content-type") || "";
    const txt = await res.clone().text().catch(() => "");

    if (!isJsMime(ct) && looksLikeHtml(txt)) {
      return { ok: false, reason: `MIME ${ct || "unknown"} dan HTML terdeteksi` };
    }

    return { ok: true, warn: !isJsMime(ct) ? `content-type non-standar: ${ct}` : "" };
  } catch (e) {
    const msg =
      e?.name === "AbortError"
        ? "Timeout fetch service-worker.js"
        : e?.message || String(e);
    return { ok: false, reason: msg };
  }
}

export function skipWaiting(registration) {
  try {
    const w = registration && registration.waiting;
    if (w) {
      w.postMessage({ type: "SKIP_WAITING" });
      return true;
    }
  } catch {}
  return false;
}

/**
 * register(config)
 * config opsional:
 * - onSuccess(reg)
 * - onUpdate(reg)
 * - onControllerChange()
 * - autoReloadOnUpdate: boolean (default true)
 * - autoReloadDelayMs: number (default 150)
 * - autoApplyUpdate: boolean (default true) -> panggil skipWaiting otomatis saat update ada
 */
export function register(config) {
  if (!isProd) return;
  if (!("serviceWorker" in navigator)) return;
  if (!isSecureContextForSW()) {
    console.warn("[SW] Tidak register: butuh HTTPS (kecuali localhost).");
    return;
  }

  // Default behavior: auto-apply + auto-reload saat update
  const autoReloadOnUpdate = config?.autoReloadOnUpdate !== false; // default true
  const autoReloadDelayMs = Number(config?.autoReloadDelayMs ?? 150);
  const autoApplyUpdate = config?.autoApplyUpdate !== false; // default true

  window.addEventListener("load", async () => {
    // Bersihkan guard lama (mis. setelah deploy sebelumnya)
    // Ini aman karena guard utama tetap pending_reload + reloaded_once
    try {
      const already = sessionStorage.getItem(SW_RELOADED_ONCE_KEY);
      const pending = sessionStorage.getItem(SW_PENDING_RELOAD_KEY);
      // kalau pernah reload, bersihkan pending supaya tidak nyangkut
      if (already && pending) sessionStorage.removeItem(SW_PENDING_RELOAD_KEY);
    } catch {}

    const base = getPublicBasePath();
    const swUrl = buildSwUrl(base);

    const v = await validateServiceWorkerUrl(swUrl);
    if (!v.ok) {
      console.warn("[SW] Tidak diregistrasi:", v.reason, "URL:", swUrl);

      // localhost: kalau invalid, biasanya rewrite/mime salah → lebih aman unregister
      if (isLocalhost) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
        } catch {}
      }
      return;
    }
    if (v.warn) console.warn("[SW] Peringatan:", v.warn);

    try {
      const registration = await navigator.serviceWorker.register(swUrl, {
        scope: base,
        updateViaCache: "none",
      });

      // sukses pertama kali (install awal)
      if (!navigator.serviceWorker.controller) {
        safeCall(config?.onSuccess, registration);
      }

      // Kalau sudah ada controller & ada waiting worker => update sudah siap
      if (navigator.serviceWorker.controller && registration.waiting) {
        safeCall(config?.onUpdate, registration);

        if (autoReloadOnUpdate) markPendingReload();
        if (autoApplyUpdate) skipWaiting(registration);
      }

      registration.onupdatefound = () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.onstatechange = () => {
          if (installing.state !== "installed") return;

          // Update tersedia (bukan install pertama)
          if (navigator.serviceWorker.controller) {
            safeCall(config?.onUpdate, registration);

            if (autoReloadOnUpdate) markPendingReload();
            if (autoApplyUpdate) skipWaiting(registration);
          } else {
            safeCall(config?.onSuccess, registration);
          }
        };
      };

      // AUTO RELOAD AMAN: hanya reload saat kita memang menandai pending_reload
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        safeCall(config?.onControllerChange);

        if (autoReloadOnUpdate) {
          reloadOnce(autoReloadDelayMs);
        }
      });

      // Optional: cek update saat tab aktif (throttle)
      let lastUpdateAt = 0;
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        const now = Date.now();
        if (now - lastUpdateAt < 30_000) return;
        lastUpdateAt = now;
        try {
          registration.update();
        } catch {}
      });
    } catch (e) {
      console.error("[SW] Gagal register:", e?.message || e);
    }

    if (isLocalhost) {
      navigator.serviceWorker.ready.catch(() => {});
    }
  });
}

export function unregister() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((reg) => reg.unregister())
    .catch(() => {});
}

/**
 * Kalau kamu butuh reset manual (mis. dari tombol "Reset PWA"),
 * kamu bisa panggil ini sebelum reload halaman.
 */
export function resetSwReloadGuards() {
  clearReloadFlags();
}
